import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CircleNotch, DownloadSimple, WarningCircle } from '@phosphor-icons/react';
import { exportElementToPdf, pdfPageWidth } from '../../utils/pdfExport';
import type { PdfOrientation } from '../../utils/pdfExport';
export interface PdfDownload {
  fileName: string;
  orientation: PdfOrientation;
  /** The print layout to capture. */
  render: () => React.ReactNode;
}
type Status = 'idle' | 'preparing' | 'error';
const BACKGROUND = '#F7F5F0';
/** Lets entrance animations settle before capturing. */
const SETTLE_MS = 1800;
export function PdfDownloadButton({
  fileName,
  orientation,
  render
}: PdfDownload) {
  const [status, setStatus] = useState<Status>('idle');
  const stageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (status !== 'preparing') return;
    let cancelled = false;
    const run = async () => {
      try {
        await document.fonts.ready;
        await new Promise(resolve => setTimeout(resolve, SETTLE_MS));
        if (cancelled || !stageRef.current) return;
        await exportElementToPdf(stageRef.current, {
          fileName,
          orientation,
          background: BACKGROUND
        });
        if (!cancelled) setStatus('idle');
      } catch {
        if (!cancelled) setStatus('error');
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [status, fileName, orientation]);
  const preparing = status === 'preparing';
  const label = preparing ? 'Preparing…' : status === 'error' ? 'Retry PDF' : 'Download PDF';
  return <>
      <button type="button" onClick={() => setStatus('preparing')} disabled={preparing} aria-label={label} aria-busy={preparing} className={['inline-flex h-9 items-center gap-2 rounded-full border px-4 font-body text-[10px] uppercase tracking-[0.16em] transition-colors', 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary', status === 'error' ? 'border-[#c2410c]/40 text-[#c2410c] hover:border-[#c2410c]' : 'border-[rgba(1,51,43,0.18)] text-background hover:border-[rgba(1,51,43,0.5)]', preparing ? 'cursor-wait opacity-70' : ''].join(' ')} data-id="element-666" data-mp="pr7akfhjmrm">
        {preparing ? <CircleNotch size={13} weight="bold" className="animate-spin" aria-hidden="true" data-id="element-667" data-mp="3cb93sxn631if" /> : status === 'error' ? <WarningCircle size={13} weight="bold" aria-hidden="true" data-id="element-668" data-mp="3hbn0xkn8cr1o" /> : <DownloadSimple size={13} weight="bold" aria-hidden="true" data-id="element-669" data-mp="3m72dm99i4gwi" />}
        <span className="hidden sm:inline" data-id="element-670" data-mp="3r8rqlugab4n8">{label}</span>
      </button>

      {preparing && createPortal(<div aria-hidden="true" className="pointer-events-none fixed left-0 top-0" style={{
      zIndex: -1,
      visibility: 'hidden'
    }} data-id="element-671" data-mp="3v8s49cvk7bt8">
            <div ref={stageRef} className="relative font-body" style={{
        width: pdfPageWidth(orientation),
        background: BACKGROUND
      }} data-id="element-672" data-mp="20wbyed6odzow">
              {render()}
            </div>
          </div>, document.body)}
    </>;
}